import {
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSCheckboxToggle,
  WSDateInput,
  WSElement,
  WSForm,
  WSModal,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  IGrowthSourceNames,
  IMemberOccupation
} from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import React, { useState } from "react";
import * as Yup from "yup";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { PaymentsSetupLayout } from "../../../Invoices/components/PaymentsSetupLayout/PaymentsSetupLayout";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import {
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_USER_PROFILE
} from "../../../query/users/keys";
import {
  useActivities,
  useMemberProfile,
  useOccupationsQuery,
  useUserProfile
} from "../../../query/users/queries";
import {
  get1099OnboardingProgress,
  getIsDomesticMember
} from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { usersService } from "../../../services/users";
import {
  AddressField,
  addressToValues,
  AddressValues,
  addressValuesToRequest
} from "../../../Settings/components/AddressField/AddressField";
import { WS_LINKS } from "../../../types/wsLinks";
import { getChangedData } from "../../../utils/getChangedData";
import { openIntercom } from "../../../utils/intercom";
import { openInNewTab } from "../../../utils/openInNewTab";
import {
  textNumbersSymbolsStringValidator,
  validatorAddress,
  validatorDOB,
  validatorFirstName,
  validatorLastName,
  validatorSSN
} from "../../../utils/validators";
import styles from "./styles.module.scss";

type PersonalDetailsFormValues = {
  firstName: string;
  lastName: string;
  preferredName?: string;
  ssn: string;
  dob?: Date;
  mcc?: string;
};

/**
 * We are hiding the Industry form field for enterprise sign-up and
 * setting the below mcc code of "Professional Services" as default industry.
 * Below value is taken from the occupations query.
 */
export const INDUSTRY_DEFAULT_VALUE = "8931"; // Professional Services

export const PersonalDetailsForm: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const activityQuery = useActivities(userId);
  const occupationsQuery = useOccupationsQuery();
  const { openModal, closeModal } = useModalContext();
  const [ssnHidden, setSsnHidden] = useState(false);
  const { openSnackbar } = useWSSnackbar();

  const [submit, submitMeta] = useWSMutation<
    any,
    AxiosError,
    PersonalDetailsFormValues
  >(
    async values => {
      const onboarding1099Progress = get1099OnboardingProgress(
        activityQuery.data
      );

      if (!memberQuery.data) throw new Error("Something went wrong");

      await usersService.user.update(userId, {
        profile: getChangedData(
          userQuery.data?.profile,
          {
            firstName: values.firstName,
            lastName: values.lastName,
            preferredName: values.preferredName,
            dob: values.dob
          },
          { dismissTimeInDates: true }
        )
      });

      const memberOccupations: IMemberOccupation[] = [];

      const matchingOccupation = (occupationsQuery.data || []).find(
        occupation => occupation.mccCode === values.mcc
      );

      if (matchingOccupation) {
        memberOccupations.push({
          taxCategory: matchingOccupation.category,
          category: matchingOccupation.category
        });
      }

      await usersService.member.update(userId, {
        memberId: userId,
        profile: getChangedData(memberQuery.data.profile, {
          mcc: values.mcc,
          ssn: values.ssn,
          taxInfo: {
            occupations: memberOccupations
          }
        })
      });

      await usersService.activity.update(userId, {
        flows: {
          onboarding1099: {
            ...onboarding1099Progress,
            currentStep: 3
          }
        }
      });
    },
    {
      dependencies: [
        QUERY_USERS_USER_PROFILE,
        QUERY_USERS_MEMBER_PROFILE,
        QUERY_USERS_ACTIVITIES
      ],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout onBack={onBack} title="Confirm tax information">
      <WSQueries
        queries={{
          userQuery,
          memberQuery,
          occupationsQuery,
          activityQuery
        }}
      >
        {({
          userQuery: { data: user },
          memberQuery: { data: member },
          occupationsQuery: { data: occupations },
          activityQuery: { data: activity }
        }) => {
          const isEnterprise =
            activity.context?.growthSource === IGrowthSourceNames.Enterprise;

          return (
            <>
              <WSForm<PersonalDetailsFormValues>
                defaultValues={{
                  firstName: user.profile.firstName,
                  lastName: user.profile.lastName,
                  preferredName: user.profile.preferredName,
                  dob: user.profile.dob,
                  ...(isEnterprise ? {} : { mcc: member.profile.mcc })
                }}
                validationSchema={Yup.object().shape({
                  firstName: validatorFirstName.required("Required"),
                  lastName: validatorLastName.required("Required"),
                  preferredName: textNumbersSymbolsStringValidator,
                  ssn:
                    member.profile.ssnProvided || ssnHidden
                      ? Yup.string()
                      : validatorSSN.required("Required"),
                  dob: validatorDOB,
                  ...(isEnterprise
                    ? {}
                    : { mcc: Yup.string().required("Required") })
                })}
                onSubmit={values => {
                  submit(
                    isEnterprise
                      ? { ...values, mcc: INDUSTRY_DEFAULT_VALUE }
                      : values
                  );
                }}
              >
                <WSForm.Field
                  name="firstName"
                  label="Legal first name"
                  component={WSTextInput}
                  mb="M"
                />

                <WSForm.Field
                  name="lastName"
                  label="Legal last name"
                  component={WSTextInput}
                  mb="M"
                />

                <WSForm.Field
                  name="preferredName"
                  label="Preferred name (optional)"
                  component={WSTextInput}
                  mb="M"
                />

                <WSElement>
                  <WSForm.Field
                    name="ssn"
                    label="SSN"
                    component={WSTextInput}
                    componentProps={{
                      type: "password",
                      allowToShowPassword: true,
                      formatOptions: {
                        numericOnly: true,
                        blocks: [9]
                      }
                    }}
                    mb="XS"
                  />

                  <WSButton.Link
                    mb="M"
                    type="button"
                    onClick={() => {
                      openModal("SSN_DESCRIPTION_MODAL");
                    }}
                  >
                    What is this used for?
                  </WSButton.Link>
                </WSElement>

                <WSForm.Field
                  name="dob"
                  label="Birthday"
                  component={WSDateInput}
                  componentProps={{
                    manualDateEntry: true
                  }}
                  mb="M"
                />

                {isEnterprise ? null : (
                  <WSForm.Field
                    name="mcc"
                    label="Industry"
                    component={WSSelect}
                    componentProps={{
                      options: occupations.map(occupation => ({
                        value: occupation.mccCode,
                        label: occupation.category
                      }))
                    }}
                    mb="M"
                  />
                )}

                <WSErrorMessage
                  mb="M"
                  contextKey="NEC1099Onboarding"
                  error={submitMeta.error}
                />

                <WSButton
                  mt="L"
                  fullWidth
                  loading={submitMeta.isLoading}
                  name="next"
                >
                  Verify personal details
                </WSButton>
              </WSForm>

              <WSModal
                name="SSN_DESCRIPTION_MODAL"
                size="S"
                title="Why does Wingspan ask for your SSN?"
              >
                <WSText mb="S">
                  This information is requested as part of Wingspan’s identity
                  verification requirements. Wingspan collects, verifies, and
                  maintains information on the individuals associated with every
                  Wingspan account, as required by our regulators, customers and
                  partners.
                </WSText>
                <WSText mb="S">
                  Our Privacy Policy further explains how and why we collect,
                  use, retain, disclose, and safeguard any personal data you
                  provide to us.
                </WSText>
                {member.profile.company?.taxId ? (
                  <WSButton.Link
                    mb="S"
                    onClick={() => {
                      closeModal("SSN_DESCRIPTION_MODAL");
                      openModal("DECLINE_SSN_MODAL");
                    }}
                  >
                    Decline to provide your SSN
                  </WSButton.Link>
                ) : (
                  <WSText mb="S">
                    If you would prefer to provide an EIN instead of an SSN,
                    please{" "}
                    <WSButton.Link
                      inline
                      onClick={() => {
                        closeModal("SSN_DESCRIPTION_MODAL");
                        onBack();
                      }}
                    >
                      update your business information
                    </WSButton.Link>{" "}
                    and provide an EIN.
                  </WSText>
                )}
                <WSText mb="M">
                  Have a question?{" "}
                  <WSButton.Link inline onClick={openIntercom}>
                    Contact support
                  </WSButton.Link>
                </WSText>
                <WSButton.Link
                  onClick={() => {
                    closeModal("SSN_DESCRIPTION_MODAL");
                  }}
                  type="button"
                >
                  Got it
                </WSButton.Link>
              </WSModal>

              <WSModal
                name="DECLINE_SSN_MODAL"
                size="S"
                title="Decline to provide your SSN"
              >
                <WSText mb="S">When you decline to provide your SSN:</WSText>
                <WSText className={styles.bulletedList} mb="M">
                  <ol>
                    <li>
                      Your company will be notified that Wingspan was unable to
                      verify your identiy.
                    </li>
                    <li>
                      You will lose access to Wingspan’s free benefits like tax
                      withholding, business banking and invoicing.
                    </li>
                  </ol>
                </WSText>

                <WSButtons>
                  <WSButton.Secondary
                    onClick={() => {
                      closeModal("DECLINE_SSN_MODAL");
                      openModal("SSN_DESCRIPTION_MODAL");
                    }}
                  >
                    Go back
                  </WSButton.Secondary>
                  <WSButton.Destructive
                    onClick={() => {
                      closeModal("DECLINE_SSN_MODAL");
                      setSsnHidden(true);
                      openSnackbar({ message: "Declined to share SSN." });
                    }}
                  >
                    Decline
                  </WSButton.Destructive>
                </WSButtons>
              </WSModal>
            </>
          );
        }}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};
